import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { DbArticleList } from "../components/viden/DbArticleList";
import { Seo, breadcrumbJsonLd, SITE_URL } from "../components/Seo";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { PRIMARY, Overline, AuroraLight, Reveal } from "../components/shared/kursusUi";
import { KNOWLEDGE_ARTICLES, KNOWLEDGE_UPDATED } from "../data/knowledgeArticles";

const hubListJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${SITE_URL}/viden#artikler`,
  name: "Votra Viden, guides til transportbranchens regler",
  numberOfItems: KNOWLEDGE_ARTICLES.length,
  itemListElement: KNOWLEDGE_ARTICLES.map((a, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${SITE_URL}/viden/${a.slug}`,
    name: a.title,
  })),
};

const KnowledgePage = () => (
  <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(210, 20%, 98%)" }}>
    <div className="votra-grain" aria-hidden />
    <Seo
      title="Viden, guides til vognmand, taxa og flextrafik | Votra"
      description="Forstå reglerne, før du vælger kursus: godsvognmand, lastbil eller varebil, transportlederens ansvar, taxachauffør og BAB-modulerne."
      path="/viden"
      jsonLd={[
        breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Viden", path: "/viden" }]),
        hubListJsonLd,
      ]}
    />
    <Header />

    <section className="hero-light relative overflow-hidden" style={{ backgroundColor: "#F4F6FB" }}>
      <AuroraLight />
      <div className="fx-scan"><span /></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10 py-16 md:py-24 text-center">
        <Reveal i={1} className="flex justify-center"><Overline light>Votra Viden</Overline></Reveal>
        <Reveal i={2}>
          <h1 className="font-display text-4xl sm:text-5xl font-medium text-white leading-tight mb-5">
            Forstå reglerne, før du vælger kursus
          </h1>
        </Reveal>
        <Reveal i={3}>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
            Korte, konkrete guides til kravene i transportbranchen, skrevet ud fra Færdselsstyrelsens
            regler og med kilder til hver påstand.
          </p>
        </Reveal>
      </div>
    </section>

    <div className="container mx-auto px-4 md:px-6 py-16 md:py-20 flex-1">
      <div className="max-w-5xl mx-auto">
        <div className="grid gap-6 md:grid-cols-2">
          {KNOWLEDGE_ARTICLES.map((a, i) => (
            <Reveal
              i={i}
              key={a.slug}
              className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-7 shadow-[0_8px_30px_rgba(15,40,90,0.06)] flex flex-col transition-shadow duration-300 hover:shadow-[0_18px_44px_rgba(15,40,90,0.13)]"
              data-testid={`knowledge-card-${a.slug}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: "hsl(210, 100%, 45%, 0.1)", color: PRIMARY }}
                >
                  <BookOpen className="h-3.5 w-3.5" /> {a.category}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="h-3.5 w-3.5" /> {a.readMinutes} min.
                </span>
              </div>
              <h2 className="font-display text-xl md:text-2xl font-medium text-slate-900 leading-snug">
                {a.title}
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed flex-1">{a.description}</p>
              <Link
                to={`/viden/${a.slug}`}
                className="mt-5 inline-flex items-center gap-2 font-semibold hover:gap-3 transition-all"
                style={{ color: PRIMARY }}
                data-testid={`knowledge-link-${a.slug}`}
              >
                Læs guiden <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          ))}
        </div>

        <p className="mt-10 text-sm text-slate-400" data-testid="knowledge-updated">
          Sidst gennemgået: {KNOWLEDGE_UPDATED}. Reglerne kan ændre sig, hver guide linker til den
          officielle kilde hos Færdselsstyrelsen og Retsinformation.
        </p>
      </div>
    </div>

    <DbArticleList company="danmark" />
    <Footer />
  </div>
);

export default KnowledgePage;
